// API Connection Service (frontend stub).
//
// The actual polling/sync logic for PGL and Starcom now runs server-side
// on Google Cloud Run (see `cloudrun/api-connection-service/`). The Cloud
// Run service writes notifications to the `api_notifications` Firestore
// collection. This module:
//
//   1. Exposes the same `notificationService` event emitter the Topbar
//      already subscribes to, so the in-app dropdown UI keeps working
//      without changes.
//   2. Bridges Firestore `api_notifications` documents into that emitter
//      via `subscribeToServerNotifications()`.

import { db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';

// In-memory event emitter that React components subscribe to.
class NotificationService {
  constructor() {
    this.listeners = [];
    this.recentNotifications = []; // Track recent notifications to prevent duplicates
    this.duplicateWindow = 2 * 60 * 1000; // 2 minutes in milliseconds
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  isDuplicate(notification) {
    const now = Date.now();

    this.recentNotifications = this.recentNotifications.filter(
      n => (now - n.timestamp) < this.duplicateWindow
    );

    if (notification.type === 'success' &&
        (notification.title === 'API Connected' || notification.title === 'API Connection Restored')) {
      return false;
    }

    const duplicate = this.recentNotifications.find(n => {
      const sameType = n.type === notification.type;
      const sameTitle = n.title === notification.title;
      const sameMessage = n.message === notification.message;
      return sameType && sameTitle && sameMessage;
    });

    return !!duplicate;
  }

  notify(notification) {
    if (this.isDuplicate(notification)) {
      return;
    }

    const notificationWithTime = {
      ...notification,
      timestamp: notification.timestamp || new Date().toISOString()
    };
    this.recentNotifications.push({
      ...notificationWithTime,
      timestamp: Date.now()
    });

    this.listeners.forEach(callback => callback(notificationWithTime));
  }
}

export const notificationService = new NotificationService();

// Bridge from Firestore `api_notifications` -> notificationService.
//
// Returns an unsubscribe function. We anchor the snapshot listener at the
// time of subscription so users only see fresh notifications, mirroring the
// previous browser-side polling behavior where alerts only fired for events
// observed during the current session.
export function subscribeToServerNotifications() {
  const startedAt = Timestamp.now();

  const notificationsQuery = query(
    collection(db, 'api_notifications'),
    where('createdAt', '>=', startedAt),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  const unsubscribe = onSnapshot(
    notificationsQuery,
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        const data = change.doc.data() || {};
        notificationService.notify({
          type: data.type || 'info',
          title: data.title || '',
          message: data.message || '',
          timestamp: data.timestamp ||
            (data.createdAt && typeof data.createdAt.toDate === 'function'
              ? data.createdAt.toDate().toISOString()
              : new Date().toISOString())
        });
      });
    },
    (error) => {
      if (error.code === 'permission-denied' || error.code === 'permissions-denied') {
        return;
      }
      console.error('api_notifications listener error:', error);
    }
  );

  return unsubscribe;
}

// Backwards-compatible facade for legacy call sites that referenced
// `apiConnectionService.startPolling()` / `stopPolling()`. Internally a
// single Firestore listener is kept so repeated `startPolling()` calls are
// idempotent and `stopPolling()` actually tears the listener down.
let activeUnsubscribe = null;

export const apiConnectionService = {
  subscribeToServerNotifications,
  startPolling: async () => {
    if (activeUnsubscribe) return;
    activeUnsubscribe = subscribeToServerNotifications();
  },
  stopPolling: () => {
    if (activeUnsubscribe) {
      activeUnsubscribe();
      activeUnsubscribe = null;
    }
  }
};
